import React, { useLayoutEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { InputMask } from "primereact/inputmask";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import Navigation from "../../components/Navigation";
import Footer from "../../components/Footer";
import { useBasket } from "../../context/BasketContext";
import { formatShortDate, capitalize, encodeArticleForUrl } from "../../helpers/helpers";
import { databaseOrders, ref, push } from "../../firebase";
import logo from "../../images/logo.svg";
import "./styles.css";

function Basket() {
    const navigate = useNavigate();
    const {
        basketItems,
        removeFromBasket,
        updateQuantity,
        clearBasket,
    } = useBasket();

    const [phoneNumber, setPhoneNumber] = useState("");
    const [name, setName] = useState("");
    const [isValid, setIsValid] = useState(true);
    const [orderCode, setOrderCode] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);

    useLayoutEffect(() => {
        document.body.style.backgroundColor = "#1D1D1D";
        window.scrollTo({ top: 0, behavior: "smooth" });
    }, []);

    const handleSubmitOrder = async () => {
        if (!phoneNumber || phoneNumber.length !== 18) {
            setIsValid(false);
            return;
        }

        setIsSubmitting(true);

        const orderObject = {
            phoneNumber,
            name,
            products: basketItems.map((item) => ({
                ...item.product,
                quantity: item.quantity,
            })),
            date: formatShortDate(new Date()),
            isHandled: false,
        };

        try {
            const ordersRef = ref(databaseOrders, "/orders");
            const newOrderRef = await push(ordersRef, orderObject);
            setOrderCode(newOrderRef.key);
            clearBasket();
        } catch (error) {
            console.error("Error creating order:", error);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (orderCode) {
        return (
            <div className="bg-white min-h-screen">
                <Navigation showBack variant="dark" />
                <div className="flex flex-col items-center justify-center py-16 px-4">
                    <div className="text-xl text-center flex flex-col justify-center items-center max-w-md">
                        <span className="text-2xl font-light mb-4">
                            Дякуємо за ваше замовлення!
                        </span>
                        <img
                            className="h-[100px] my-4"
                            src={logo}
                            alt="TSATSKY"
                            loading="lazy"
                            decoding="async"
                        />
                        <span className="text-gray-600">
                            Невдовзі ми зв'яжемося з вами для підтвердження.
                        </span>
                        <div className="mt-4 text-sm text-gray-400">
                            Код замовлення: {orderCode}
                        </div>
                        <Button
                            label="Продовжити покупки"
                            onClick={() => navigate("/catalog")}
                            className="basket-order-button mt-8 px-8 py-3"
                        />
                    </div>
                </div>
            </div>
        );
    }

    if (basketItems.length === 0) {
        return (
            <div className="bg-white min-h-screen">
                <Navigation showBack variant="dark" />
                <div className="flex flex-col items-center justify-center py-16 px-4">
                    <i className="pi pi-shopping-cart text-6xl text-gray-300 mb-4" />
                    <h2 className="text-2xl font-light text-gray-600 mb-2">
                        Кошик порожній
                    </h2>
                    <p className="text-gray-400 mb-8">
                        Додайте товари з каталогу
                    </p>
                    <Button
                        label="Перейти до каталогу"
                        onClick={() => navigate("/catalog")}
                        className="basket-order-button px-8 py-3"
                    />
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white min-h-screen">
            <Navigation showBack variant="dark" />

            <div className="max-w-4xl mx-auto px-4 py-8">
                <h1 className="text-2xl lg:text-3xl font-light text-gray-800 mb-8">
                    Кошик
                </h1>

                {/* Basket Items */}
                <div className="space-y-4 mb-8">
                    {basketItems.map(({ product, quantity }) => (
                        <div
                            key={product.article}
                            className="flex gap-4 p-4 border border-gray-200 rounded-lg"
                        >
                            {/* Product Image */}
                            <img
                                src={product.image}
                                alt={product.imageAlt || product.name}
                                className="w-24 h-24 object-cover rounded cursor-pointer"
                                loading="lazy"
                                decoding="async"
                                onClick={() => {
                                    const collectionPath = product.collection?.toLowerCase();
                                    const encodedArticle = encodeArticleForUrl(product.article);
                                    navigate(
                                        collectionPath
                                            ? `/catalog/${collectionPath}/${encodedArticle}`
                                            : `/catalog/${encodedArticle}`
                                    );
                                }}
                            />

                            {/* Product Info */}
                            <div className="flex-1 flex flex-col justify-between">
                                <div>
                                    <h3 className="text-lg font-medium text-gray-800">
                                        {product.name}
                                    </h3>
                                    <p className="text-sm text-gray-400">
                                        Артикул: {product.article}
                                    </p>
                                </div>

                                {/* Price hidden */}

                                {/* Quantity Controls */}
                                <div className="flex items-center gap-2 mt-2">
                                    <button
                                        onClick={() =>
                                            updateQuantity(
                                                product.article,
                                                quantity - 1
                                            )
                                        }
                                        className="w-8 h-8 flex items-center justify-center border border-gray-300 rounded hover:bg-gray-100"
                                    >
                                        -
                                    </button>
                                    <span className="w-8 text-center">
                                        {quantity}
                                    </span>
                                    <button
                                        onClick={() =>
                                            updateQuantity(
                                                product.article,
                                                quantity + 1
                                            )
                                        }
                                        className="w-8 h-8 flex items-center justify-center border border-gray-300 rounded hover:bg-gray-100"
                                    >
                                        +
                                    </button>
                                </div>
                            </div>

                            {/* Remove Button */}
                            <button
                                onClick={() => removeFromBasket(product.article)}
                                className="text-gray-400 hover:text-red-500 p-2"
                                aria-label="Видалити"
                            >
                                <i className="pi pi-trash" />
                            </button>
                        </div>
                    ))}
                </div>

                {/* Total hidden */}

                {/* Checkout Form */}
                <div className="border-t border-gray-200 pt-8">
                    <h2 className="text-xl font-light text-gray-800 mb-6">
                        Оформлення замовлення
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                        <div>
                            <label
                                htmlFor="phone"
                                className="block text-sm font-medium text-gray-700 mb-2"
                            >
                                Номер телефону*
                            </label>
                            <InputMask
                                id="phone"
                                value={phoneNumber}
                                onChange={(e) => {
                                    setIsValid(true);
                                    setPhoneNumber(e.target.value);
                                }}
                                className={`w-full bg-gray-100 p-4 shadow-none rounded ${
                                    !isValid ? "border-2 border-red-500" : ""
                                }`}
                                mask="(+999) 99-999-9999"
                                placeholder="(+380) 99-999-9999"
                            />
                            {!isValid && (
                                <span className="text-red-500 text-sm mt-1">
                                    Введіть номер телефону
                                </span>
                            )}
                        </div>

                        <div>
                            <label
                                htmlFor="name"
                                className="block text-sm font-medium text-gray-700 mb-2"
                            >
                                Ім'я
                            </label>
                            <InputText
                                id="name"
                                value={name}
                                onChange={(e) => setName(capitalize(e.target.value))}
                                className="w-full bg-gray-100 p-4 shadow-none rounded"
                                placeholder="Ваше ім'я"
                            />
                        </div>
                    </div>

                    <Button
                        label={isSubmitting ? "Оформлення..." : "Оформити замовлення"}
                        onClick={handleSubmitOrder}
                        disabled={isSubmitting}
                        className="basket-order-button w-full py-4 text-lg font-semibold tracking-wider"
                    />
                </div>
            </div>

            <Footer containerClassNames="bg-darkgray text-harmony" isDark />
        </div>
    );
}

export default Basket;
